import { type ReactNode } from "react";
import {
  Alert,
  Box,
  type TabProps,
  Tabs
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import {
  useGetAccountAccess,
  useGetUserInfo
} from "@dealer-center/ddc-pages-configuration-fetch";
/*
 * The detail panel comes from the neutral shared surface (`../components/UserInfo`), not from
 * `./components/` — it was extracted there so `UserManager/` can render the same panel without a
 * forbidden sibling→sibling import (Principle II, tasks.md Phase 2). Only `PageHeading` and
 * `HeaderActions` remain area-private to My Info.
 */
import {
  AccountAccessTab,
  BORDER_MUTED,
  FeatureAccessTab,
  IdentityHeader,
  LoginHistoryTab,
  SPACE_BLOCK,
  SPACE_SECTION,
  SURFACE_DEFAULT,
  SURFACE_MUTED,
  StatusRegion,
  UnsavedChangesPrompt,
  UserInfoAreaContext,
  type UserInfoTabId,
  UserInformationTab,
  hasAccountHierarchy,
  usePendingEditsGuard,
  useTabScrollButtons,
  useUserInfoEdits,
  useUserInfoSave,
  useUserInfoTabs
} from "../components/UserInfo";
import { HeaderActions } from "./components/PageHeader/HeaderActions";
import { PageHeading } from "./components/PageHeader/PageHeading";
import { useMyInfoSubject } from "./useMyInfoSubject";

const TABS_ID = "myinfo-tabs";

/**
 * How the shared panel attributes its telemetry while this page renders it.
 *
 * **These two values are My Info's shipped New Relic action names and GTM event names**, so this
 * provider changes nothing that was already being emitted — it only stops the panel *assuming* them
 * now that User Manager renders it too. Do not "simplify" it away by making the context default to
 * My Info: the default is neutral on purpose (see `userInfoAreaContext`).
 *
 * Module scope, so its identity is stable and the provider never re-renders its subtree for a new
 * object.
 */
const MY_INFO_AREA = { label: "My Info", slug: "myinfo" };

/**
 * The My Info page shell: one subject, the identity header, and the tab set.
 *
 * `useMyInfoSubject` resolves the subject **once** here and every tab receives it as a prop. No hook
 * below this point re-reads the outlet context (contracts/fetch-hooks.md invariant 4) — two readers
 * could drift, and in an "as" context drifting means showing one user's header over another's data.
 *
 * The subject hook **throws** when the outlet context is absent rather than returning a partial
 * subject. That is not a case this component handles: `PagesControlcenterApp` renders a loading page
 * instead of the `<Outlet />` until `ccUserInfo` exists, and `Configuration` forwards that context
 * through (anchored by `routes.spec.tsx`). A throw here means the page was mounted outside
 * ControlCenter, which is a wiring error and should be loud, not a blank form.
 */
export const MyInfo = () => {
  const subject = useMyInfoSubject();

  /*
   * FR-006's conditional Account Access tab, sequenced per research R-007.
   *
   * The query lives **here**, not in the tab, because the tab strip has to render before it resolves:
   * `accountaccess` is the slowest endpoint (~5–6s), and gating the strip on it would leave the page
   * empty for seconds — which the Edge Cases forbid outright ("never a momentarily empty tab"). So the
   * tab appears immediately in a loading state and is removed **only once the query has resolved** to
   * "no hierarchy". A single-account user therefore sees a tab that then disappears; that was accepted
   * deliberately as the lesser evil.
   *
   * `isSuccess` is the gate, not `!isLoading`: on **failure** the tab stays. A failed read tells us
   * nothing about whether a hierarchy exists, and removing the tab would report "you have no account
   * hierarchy" — inventing an answer from an absent response. The tab stays and shows its own failure,
   * which also keeps SC-010 honest.
   *
   * React Query dedupes this against the tab's own identical query key, so mounting the tab costs no
   * second request.
   */
  const accountAccess = useGetAccountAccess({
    userId: subject.userId,
    accountId: subject.accountId
  });
  const isAccountAccessUnavailable =
    accountAccess.isSuccess && !hasAccountHierarchy(accountAccess.data);

  const { tabs, activeTabId, selectTab } = useUserInfoTabs({
    unavailableTabIds: isAccountAccessUnavailable ? ["account"] : []
  });

  /*
   * Pending User Information edits, owned **here** rather than in the tab.
   *
   * FR-009: "Switching tabs MUST preserve pending edits on User Information, MUST NOT discard them."
   * The panel handed to an inactive tab below is an empty fragment, so `UserInformationTab` unmounts
   * on every tab switch — state held inside it would vanish the moment the user opened Login History.
   * Owning it at the page level is what makes the guarantee hold. See `useUserInfoEdits`.
   */
  const userInfoEdits = useUserInfoEdits();

  /*
   * The stored record, read here so Save and the leave-the-page guard can ask whether the form is
   * dirty. React Query dedupes it against the tab's identical request, so this adds no fetch.
   */
  const userInfo = useGetUserInfo({
    userId: subject.userId,
    accountId: subject.accountId
  });

  // The save is shared with User Manager; `HeaderActions` announces its success.
  const save = useUserInfoSave({
    userId: subject.userId,
    accountId: subject.accountId,
    summary: userInfo.data?.summary,
    edits: userInfoEdits,
    activeTabId,
    selectTab,
    isSubjectTheOperator: subject.isSubjectTheOperator
  });

  // FR-024. Tab switches only change `?tab=`, so they never prompt (FR-009).
  const pendingEdits = usePendingEditsGuard({
    isDirty: save.isFormDirty,
    isSaving: save.isSaving,
    onDiscard: userInfoEdits.reset
  });

  const handleKeepEditing = () => {
    pendingEdits.cancel();
  };

  const handleDiscardEdits = () => {
    pendingEdits.confirmDiscard();
  };

  /*
   * Building all four elements is cheap and mounts nothing — a JSX element is a descriptor, and only
   * the one handed to the active tab below is ever rendered. That matters: mounting every panel would
   * fire all four queries on page load, including `loginhistory` at ~20s, for tabs the user may never
   * open.
   *
   * All four panels are built as of Phase 6.
   */
  const panels: Record<UserInfoTabId, ReactNode> = {
    userinfo: (
      <UserInformationTab
        edits={userInfoEdits}
        errors={save.fieldErrorMessages}
        isReadOnly={save.isSaving}
        subject={subject}
      />
    ),
    /*
     * `isSubjectTheOperator` licenses the first-person copy ("your sign-in history", "the accounts
     * your login can reach"). It is false while an internal user is acting as someone, when the
     * subject is the as-user (`useMyInfoSubject`). The shared tabs default it to `false`, so a
     * consumer who forgets it gets impersonal copy rather than a false claim.
     */
    account: (
      <AccountAccessTab
        isSubjectTheOperator={subject.isSubjectTheOperator}
        subject={subject}
      />
    ),
    feature: <FeatureAccessTab subject={subject} />,
    login: (
      <LoginHistoryTab
        isSubjectTheOperator={subject.isSubjectTheOperator}
        subject={subject}
      />
    )
  };

  /*
   * Interstate renders a `role="tabpanel"` for every tab carrying a truthy `component`, hiding the
   * inactive ones — so each tab needs *something* there to stay selectable (its `value` is dropped
   * otherwise), and the active one gets the real content.
   */
  const interstateTabs: TabProps[] = tabs.map(tab => ({
    active: tab.id === activeTabId,
    component: tab.id === activeTabId ? panels[tab.id] : <></>,
    id: tab.id,
    label: tab.label,
    value: tab.id
  }));

  // Interstate's Tabs misreports its own scroll-chevron overflow check; the shared hook recomputes it. See `useTabScrollButtons`.
  useTabScrollButtons(TABS_ID);

  return (
    <UserInfoAreaContext.Provider value={MY_INFO_AREA}>
      <Box
        data-testid={"myinfo-page"}
        sx={{
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          minHeight: 0
        }}
      >
        <PageHeading />

        <UnsavedChangesPrompt
          isOpen={pendingEdits.isPromptOpen}
          isSubjectTheOperator={subject.isSubjectTheOperator}
          onCancel={handleKeepEditing}
          onDiscard={handleDiscardEdits}
        />

        {/*
         * The card. Identity row, tab strip, and panels are one bordered surface on the app
         * background, per the approved prototype's `.um-detail` — which is what makes the tabs read as
         * belonging to this subject rather than floating on the page.
         */}
        <Box
          data-testid={"myinfo-card"}
          sx={{
            backgroundColor: SURFACE_DEFAULT,
            border: `1px solid ${BORDER_MUTED}`,
            borderRadius: "8px",
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden"
          }}
        >
          {/*
           * T069's shared live region wraps the identity row and the tab set, so it is mounted on page
           * load and survives every tab switch — a region that mounts carrying its first message is the
           * one least likely to be announced. Each tab writes to it through `TabState`, and the header
           * actions announce their writes into it; see `StatusRegion`. It renders a fragment, so the
           * card's flex layout is unchanged.
           */}
          <StatusRegion>
            {/*
             * FR-008's single page-header action slot — in the card's first row, opposite the
             * identity, so the one Save is visible from every tab and no tab needs its own.
             *
             * `flexWrap` plus `marginLeft: auto` rather than `justifyContent: space-between`: at a
             * narrow width the actions drop below the identity and stay left-aligned with it, whereas
             * `space-between` would strand them against the right edge on their own line.
             */}
            <Box
              sx={{
                alignItems: "center",
                borderBottom: `1px solid ${SURFACE_MUTED}`,
                columnGap: SPACE_BLOCK,
                display: "flex",
                flexShrink: 0,
                flexWrap: "wrap",
                padding: SPACE_SECTION,
                rowGap: SPACE_BLOCK
              }}
            >
              <IdentityHeader subject={subject} />

              <Box
                data-testid={"myinfo-header-actions"}
                sx={{ marginLeft: "auto" }}
              >
                <HeaderActions
                  accountId={subject.accountId}
                  isDirty={save.isFormDirty}
                  isSaving={save.isSaving}
                  isSubjectTheOperator={subject.isSubjectTheOperator}
                  userId={subject.userId}
                  onSave={save.handleSave}
                />
              </Box>
            </Box>

            {/* Above the tabs so it shows from every tab; `role="alert"` is the announcement. */}
            {save.saveFailureMessage ? (
              <Box
                sx={{
                  flexShrink: 0,
                  padding: `${SPACE_BLOCK} ${SPACE_SECTION} 0`
                }}
              >
                <Alert
                  data-testid={"myinfo-save-error"}
                  role={"alert"}
                  type={"error"}
                >
                  {save.saveFailureMessage}
                </Alert>
              </Box>
            ) : null}

            {/*
             * The strip is inset to align with the identity row above it and the panel content below
             * it; `TabState` owns the panels' own padding, since Interstate renders the panel element
             * itself.
             *
             * The tab strip spans the card edge to edge, so its underline reads as the card's own
             * divider rather than a rule floating inside it — the prototype's `.tabs` treatment.
             *
             * The margin reset is not cosmetic tidying. Interstate gives every `role="tabpanel"` its
             * own `margin-left: 8px; margin-top: 16px`, which stacks on top of `TabState`'s padding and
             * left every tab's content 8px to the right of the identity row above it — a misalignment
             * that reads as a rendering bug precisely because it is small. Zeroing it here makes
             * `TabState`'s padding the single source of the panels' inset. Targeted by `role` rather
             * than by class name, so it survives an Interstate class-hash change.
             *
             * The flex/overflow rules on this box and on `[role='tabpanel']` are what let the tab
             * content scroll on its own while the tab strip above it stays put: Interstate renders the
             * tab bar and each panel as flat siblings with no height/flex styling of its own (there is
             * no prop for it), so this box has to both establish the flex column and hand the panel the
             * remaining space. The tab bar keeps its natural size as the other flex child.
             */}
            <Box
              sx={{
                display: "flex",
                flex: "1 1 auto",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
                "& [role='tabpanel']": {
                  flex: "1 1 auto",
                  marginLeft: 0,
                  marginTop: 0,
                  minHeight: 0,
                  overflowY: "auto"
                }
              }}
            >
              <Tabs
                ariaLabel={Locale.getText("TABS_ARIA_LABEL") as string}
                id={TABS_ID}
                tabs={interstateTabs}
                onActivate={selectTab}
              />
            </Box>
          </StatusRegion>
        </Box>
      </Box>
    </UserInfoAreaContext.Provider>
  );
};
